import { useTranslation } from 'react-i18next'
import { Select } from '@/components/v2/ui/Select'
import { Input } from '@/components/v2/ui/Input'
import { Button } from '@/components/v2/ui/Button'
import type { SegmentCondition, SegmentOp } from './segmentPresets'

interface SegmentRuleValueControlProps {
  condition: SegmentCondition
  isBool: boolean
  isListedOnChannel: boolean
  isNumericChannel: boolean
  onChange: (c: SegmentCondition) => void
}

/** Op indicator/select + value control (bool button, listed-select, or numeric input) for a rule row. */
export function SegmentRuleValueControl({
  condition, isBool, isListedOnChannel, isNumericChannel, onChange,
}: SegmentRuleValueControlProps) {
  const { t } = useTranslation()
  const { field, value, op } = condition

  return (
    <>
      {isNumericChannel ? (
        <div className="w-20 shrink-0">
          <Select
            data-testid="rule-op"
            options={[
              { value: 'gte', label: t('productSegments.ops.gte') },
              { value: 'lte', label: t('productSegments.ops.lte') },
            ]}
            value={op}
            onValueChange={(v) => onChange({ ...condition, op: v as SegmentOp })}
            aria-label={t('productSegments.builder.opLabel')}
          />
        </div>
      ) : (
        <span className="text-sm text-neutral-500 dark:text-neutral-400 shrink-0 px-1">
          {isBool || isListedOnChannel ? t('productSegments.builder.op.eq') : t('productSegments.builder.op.gte')}
        </span>
      )}

      {isListedOnChannel ? (
        <div className="w-36 shrink-0">
          <Select
            data-testid="rule-bool-select"
            options={[
              { value: 'true', label: t('productSegments.listed.yes') },
              { value: 'false', label: t('productSegments.listed.no') },
            ]}
            value={String(!!value)}
            onValueChange={(v) => onChange({ ...condition, value: v === 'true' })}
            aria-label={t('productSegments.builder.toggleBool')}
          />
        </div>
      ) : isBool ? (
        <Button
          data-testid="rule-bool"
          variant="outline"
          size="sm"
          onClick={() => onChange({ field, op: 'eq', value: !value })}
          aria-label={t('productSegments.builder.toggleBool')}
          aria-pressed={!!value}
        >
          {value ? t('productSegments.builder.boolTrue') : t('productSegments.builder.boolFalse')}
        </Button>
      ) : (
        <div className="w-28 shrink-0">
          <Input
            data-testid="rule-value"
            type="number"
            min={0}
            value={Number(value)}
            // Number('') === 0; kein NaN möglich (Browser normalisiert Teileingabe zu '')
            onChange={(e) => onChange({ ...condition, value: Number(e.target.value) })}
            aria-label={t('productSegments.builder.valueLabel')}
          />
        </div>
      )}
    </>
  )
}

SegmentRuleValueControl.displayName = 'SegmentRuleValueControl'
